<template>
  <v-container fluid>
    <v-row v-if="isLoading">
      <v-col>
        <loading-spinner></loading-spinner>
      </v-col>
    </v-row>
    <v-row v-else>
      <v-col cols="12" md="6">
        <paginated-table
          :data="conferences"
          :columns="tableColumns"
          :title="$t('title.conference_management')"
          @updatePagination="updatePagination"
        ></paginated-table>
      </v-col>

      <v-col cols="12" md="6">
        <v-form ref="form" v-model="valid">
          <editor-card
            :value="conference.description"
            @update-content="updateContent"
          >
            <template v-slot:header>
              <v-row class="mt-2 mb-2">
                <v-col cols="12">
                  <v-text-field
                    v-model="conference.title"
                    :rules="[rules.required]"
                    dense
                    class="mt-2"
                    :label="$t('label.title')"
                  >
                    <template v-slot:append>
                      <v-icon v-if="!conference.title" color="error"
                        >mdi-close</v-icon
                      >
                      <v-icon v-else color="success">mdi-check</v-icon>
                    </template>
                  </v-text-field>
                </v-col>
              </v-row>
            </template>

            <template v-slot:bottom-content>
              <v-row class="mt-2">
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="conference.started_at"
                    :label="$t('label.started_at')"
                    type="date"
                    :rules="[rules.required]"
                    outlined
                    dense
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="conference.ended_at"
                    :label="$t('label.ended_at')"
                    type="date"
                    :rules="[rules.required]"
                    outlined
                    dense
                  ></v-text-field>
                </v-col>
              </v-row>

              <v-row class="mt-0">
                <v-col cols="12">
                  <v-text-field
                    v-model="conference.down_file_name"
                    :label="$t('label.file_name')"
                    outlined
                    dense
                  ></v-text-field>
                </v-col>
              </v-row>

              <v-row class="mt-0">
                <v-col cols="12">
                  <upload-file
                    :initialFilePath="conference.pdf_path"
                    @update-saved-file-path="updateSavedFilePath"
                  ></upload-file>
                </v-col>
              </v-row>
            </template>

            <template v-slot:bottom-buttons>
              <v-card-actions class="mt-3">
                <v-spacer></v-spacer>
                <custom-button
                  @click="handleSubmit"
                  :label="$t('button.save')"
                  :disabled="!valid"
                  color="primary"
                  size="lg"
                  gradient
                  rounded
                ></custom-button>
              </v-card-actions>
            </template>
          </editor-card>
        </v-form>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import EditorCard from "@/components/common/Cards/EditorCard.vue";
import PaginatedTable from "@/components/Tables/PaginatedTable.vue";
import UploadFile from "@/components/Forms/UploadFile.vue";
import CustomButton from "@/components/common/buttons/CustomButton.vue";
import LoadingSpinner from "@/components/common/Spinners/LoadingSpinner.vue";
import { getConferenceManagementColumns } from "@/views/ConferenceView/columns";
import { savePopup, success } from "@/views/ConferenceView/popups";

import { conferenceApi } from "@/api/api";

export default {
  components: {
    PaginatedTable,
    CustomButton,
    EditorCard,
    UploadFile,
    LoadingSpinner,
  },
  data() {
    return {
      isLoading: false,
      tableColumns: getConferenceManagementColumns({
        edit: this.handleEdit,
      }),
      conferences: {
        page_info: { page: 1, size: 5, total_elements: 5, total_pages: 1 },
        elements: [],
      },
      conference: {
        idx: null,
        title: null,
        description: null,
        started_at: null,
        ended_at: null,
        down_file_name: null,
        pdf_path: null,
      },
      valid: false,
      rules: {
        required: (v) => !!v || "This field is required",
      },
    };
  },
  methods: {
    handleEdit(post) {
      this.fetchConference(post);
    },
    async handleSubmit() {
      const isConfirmed = await savePopup();
      if (!isConfirmed) return;

      const isSuccess =
        this.conference.idx == null
          ? await this.createConference(this.conference)
          : await this.updateConference(this.conference);

      if (isSuccess) {
        await this.fetchConferences();
        await success();
      }
    },
    fetchConferences() {
      this.isLoading = true;
      conferenceApi
        .getConferences({
          size: this.conferences.page_info.size,
          page: this.conferences.page_info.page,
        })
        .then((response) => {
          this.conferences = response.data.response;
          this.isLoading = false;
        })
        .catch(() => (this.isLoading = false));
    },
    fetchConference(conference) {
      this.isLoading = true;
      conferenceApi
        .getConference(conference)
        .then((response) => {
          this.conference = response.data.response;
          this.isLoading = false;
        })
        .catch(() => (this.isLoading = false));
    },
    async createConference(conference) {
      return conferenceApi
        .createConference(conference)
        .then((response) => response.status === 201);
    },
    async updateConference(conference) {
      return conferenceApi
        .updateConference(conference)
        .then((response) => response.status === 200);
    },
    updateContent(newValue) {
      this.conference.description = newValue;
    },
    updatePagination(pagination) {
      this.conferences.page_info = {
        ...this.conferences.page_info,
        ...pagination,
      };
      this.fetchConferences();
    },
    updateSavedFilePath(newPath) {
      this.conference.pdf_path = newPath;
    },
  },
  mounted() {
    this.fetchConferences();
  },
};
</script>
